// Core Libraries
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";

//Third-Party Librarie
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { GripVertical } from "lucide-react";
import removeIcon from "../../assets/icons/removeIcon.png";
import deleteIcon from "../../assets/icons/deleteIcon.png";
// Components
import Input from "../../components/Form/Input";
import FileUpload from "../../components/Form/FileUpload";
import Dropdown from "../../components/Form/Dropdown";
import DropdownWithInput from "../../components/Form/DropdownWithInput";
import CustomButton from "../../components/Buttons/CustomButton";
import MultiSelectedDropDownWithInput from "../../components/Form/MultiSelectedDropDownWithInput";

// Custom Hooks & Utilities
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import { uploadToCloudflare } from "../../store/thunks/cloudFlareThunks";

// Redux Actions/Thunks
import {
  checkDetailedDuplicateTrack,
  checkDuplicateTracks,
  createAndUpdateTrack,
  getArtists,
  getRemixers,
  getTags,
  getTrackById,
  refetchTrackFeatures,
} from "../../services/thunks/TrackThunks";
import { getRemixerUserById } from "../../services/thunks/remixerUserThunks";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import { formStyles } from "../../components/Form/FormUtils";
import {
  formatDateForBackend,
  formatDateForInput,
  localDateAsUtcMidnight,
} from "../../utils/dateFormating";
import { generatePeaksFromURL } from "../../utils/generateSongPeeks";
import {
  isAlphabetsNumbersSpacesOnly,
  isAsciiOnly,
  isEmojiFree,
  validateArtistName,
  validateRemixerName,
  validateTag,
} from "../../utils/validationUtils";
import { sanitizeHyphens } from "../../utils/formatting";
import { fetchExternalImageAsFile } from "../../utils/fetchExternalImageAsFile";
const { roles } = require("../../constants/commonConstants");

// FEATURE FLAG: Set to true when the client requests and pays for the drag-and-drop track version ordering feature.
const ENABLE_DRAG_AND_DROP = true;

const DraggableVersionRow = ({
  children,
  versionIndex,
  remixIndex,
  moveVersion,
  isOwnerOfEdit
}) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: `version-row-${remixIndex}`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = versionIndex;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveVersion(remixIndex, dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: `version-row-${remixIndex}`,
    item: () => {
      return { id: versionIndex, index: versionIndex };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isOwnerOfEdit && ENABLE_DRAG_AND_DROP
  });

  const opacity = isDragging ? 0 : 1;
  drop(ref);
  preview(ref);

  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId} className="flex flex-row gap-2 items-start w-full">
      {isOwnerOfEdit && ENABLE_DRAG_AND_DROP && (
        <div ref={drag} className="cursor-grab hover:text-gray-400 text-[#505050] mr-1 mt-[43px]">
          <GripVertical size={20} />
        </div>
      )}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
};

const AddNewTracks = () => {
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { id: trackId } = useParams();
  const { role } = auth;
  const isRemixer = role === roles.REMIXER;
  const isEditMode = !!trackId;
  const isRemixerEditing = isRemixer && isEditMode;
  const [isCancelling, setIsCancelling] = useState(false);
  const publishingStepsRef = useRef([]);
  const isCancelledRef = useRef(false);
  const didEnrichOnceRef = useRef(false);

  // Custom Hooks
  const [doGetTrackById] = useThunk(getTrackById);
  const [doCreateAndUpdateTrackForm, isCreatingAndUpdatingTrack] =
    useThunk(createAndUpdateTrack);
  const [doGetArtists] = useThunk(getArtists);
  const [doGetRemixers] = useThunk(getRemixers);
  const [doGetTags] = useThunk(getTags);
  const [doCheckDetailedDuplicateTrack, isCheckingDetailedDuplicateTrack] =
    useThunk(checkDetailedDuplicateTrack);
  const [doUploadToCloudflare, isUploadingToCloudflare] =
    useThunk(uploadToCloudflare);
  const [doGetRemixerUserById] = useThunk(getRemixerUserById);
  const [doRefetchTrackFeatures, isRefetchingTrackFeatures] =
    useThunk(refetchTrackFeatures);

  // Local States and Refs
  const [form, setForm] = useState({
    trackTitle: "",
    artist: [],
    danceability: "",
    energy: "",
    popularity: "",
    valence: "",
    coverArt: null,
  });
  const [remixInfos, setRemixInfos] = useState([
    {
      editTitle: null,
      remixer: null,
      additionalMainArtists: [],
      featuredArtist: [],
      releaseDate: "",
      releaseYear: "",
      bpm: "",
      genre: null,
      key: null,
      tags: [],
      versions: [{ versionType: "", audioFile: null }],
    },
  ]);
  const [remixers, setRemixers] = useState([]);
  const [artistOptions, setArtistOptions] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVersionDeleteModal, setShowVersionDeleteModal] = useState(false);
  const [metadataPopulatedFields, setMetadataPopulatedFields] = useState({});
  const [pendingVersionDelete, setPendingVersionDelete] = useState({
    remixIndex: null,
    versionIndex: null,
  });
  const [genres, setGenres] = useState([
    { _id: "1", name: "Hip-Hop / R&B" },
    { _id: "2", name: "Latin" },
    { _id: "3", name: "Electronic / Dance" },
    { _id: "4", name: "International" },
    { _id: "5", name: "Reggae / Dancehall" },
    { _id: "6", name: "Pop / Top 100" },
  ]);
  const [keys, setKeys] = useState([
    { _id: "1", name: "1A" },
    { _id: "2", name: "1B" },
    { _id: "3", name: "2A" },
    { _id: "4", name: "2B" },
    { _id: "5", name: "3A" },
    { _id: "6", name: "3B" },
    { _id: "7", name: "4A" },
    { _id: "8", name: "4B" },
    { _id: "9", name: "5A" },
    { _id: "10", name: "5B" },
    { _id: "11", name: "6A" },
    { _id: "12", name: "6B" },
    { _id: "13", name: "7A" },
    { _id: "14", name: "7B" },
    { _id: "15", name: "8A" },
    { _id: "16", name: "8B" },
    { _id: "17", name: "9A" },
    { _id: "18", name: "9B" },
    { _id: "19", name: "10A" },
    { _id: "20", name: "10B" },
    { _id: "21", name: "11A" },
    { _id: "22", name: "11B" },
    { _id: "23", name: "12A" },
    { _id: "24", name: "12B" },
  ]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [remixErrors, setRemixErrors] = useState({});
  const [currentRemixer, setCurrentRemixer] = useState({});
  const [tagErrors, setTagErrors] = useState({});

  const location = useLocation();
  const fromAddPlaylist = location.state?.fromAddPlaylist;
  const previousFormData = location.state?.formData;
  const previousAssignedTracks = location.state?.assignedTracks;

  useEffect(() => {
    const fetchDropdownData = async () => {
      const [artistsRes, remixersRes, tagsRes] = await Promise.allSettled([
        doGetArtists({ token: auth.token }),
        doGetRemixers({ token: auth.token }),
        doGetTags({ token: auth.token }),
      ]);

      const normalizedArtists =
        artistsRes.status === "fulfilled" &&
        Array.isArray(artistsRes.value.response?.items)
          ? artistsRes.value.response.items.map((a) => ({
              _id: a._id,
              name: a.artistName,
            }))
          : [];

      const normalizedRemixers =
        remixersRes.status === "fulfilled" &&
        Array.isArray(remixersRes.value.response.data?.items)
          ? remixersRes.value.response.data.items.map((r) => ({
              _id: r._id,
              name: r.remixerName,
            }))
          : [];

      const normalizedTags =
        tagsRes.status === "fulfilled" &&
        Array.isArray(tagsRes.value.response?.data.items)
          ? tagsRes.value.response.data.items
              .filter((t) => t.tagName.toLowerCase() !== "unavailable")
              .map((t) => ({
                _id: t._id,
                name: t.tagName,
              }))
          : [];

      setArtistOptions(normalizedArtists);
      setRemixers(normalizedRemixers);
      setTagsOptions(normalizedTags);
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (trackId) {
      loadTrack(trackId);
    } else {
      setForm({
        trackTitle: "",
        artist: [],
        coverArt: null,
        danceability: "",
        energy: "",
        popularity: "",
        valence: "",
        remixInfos: [],
      });
      setRemixInfos([
        {
          editTitle: null,
          remixer: null,
          additionalMainArtists: [],
          featuredArtist: [],
          releaseDate: "",
          releaseYear: "",
          bpm: "",
          genre: null,
          key: null,
          tags: [],
          versions: [{ versionType: "", audioFile: null }],
        },
      ]);
      setMetadataPopulatedFields({});
      setFormErrors({});
      setRemixErrors({});
      setFormErrors({});
      setCurrentRemixer({});
      setPendingDeleteIndex(null);
      setPendingVersionDelete({ remixIndex: null, versionIndex: null });
    }
  }, [trackId]);

  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    const fetchCurrentRemixerUser = async () => {
      if (!isRemixer || !auth.user?._id) return;

      const res = await doGetRemixerUserById({
        token: auth.token,
        id: auth.user._id,
      });

      if (!res?.success) {
        return;
      }
      setCurrentRemixer(res?.response.data);
    };

    fetchCurrentRemixerUser();
  }, [auth.token, auth.user?._id, isRemixer]);

  useEffect(() => {
    if (
      !isEditMode &&
      isRemixer &&
      currentRemixer?.remixerProfileId &&
      remixInfos.length === 1 &&
      !remixInfos[0].remixer
    ) {
      const matchedRemixer = remixers.find(
        (r) => r._id === currentRemixer.remixerProfileId,
      );

      if (matchedRemixer) {
        setRemixInfos((prev) => {
          const updated = [...prev];
          updated[0].remixer = matchedRemixer;
          return updated;
        });
      }
    }
  }, [isRemixer, currentRemixer, remixInfos, remixers]);

  // Event Handlers
  const loadTrack = async (id) => {
    const res = await doGetTrackById({ token: auth.token, id });
    const track = res.response;
    console.log("LoadTrack", track);

    const populatedRemixInfos = (track.remixInfos || []).map((info) => {
      let mappedGenre = null;
      if (info.genre) {
        mappedGenre = genres.find((g) => g.name === info.genre);

        if (!mappedGenre) {
          mappedGenre = genres.find(
            (g) => g.name.toLowerCase() === info.genre.toLowerCase(),
          );
        }
      }

      return {
        editTitle: info.editTitle || "",
        remixer: info.remixer
          ? {
              _id: info.remixer._id,
              name: info.remixer?.remixerName,
            }
          : null,
        featuredArtist:
          info.featuredArtist?.map((a) => ({
            _id: a._id,
            name: a.artistName || a.name,
          })) || [],
        additionalMainArtists:
          info.additionalMainArtists?.map((a) => ({
            _id: a._id,
            name: a.artistName || a.name,
          })) || [],
        releaseDate: formatDateForInput(info.releaseDate),
        releaseYear: info.releaseYear?.toString() || "",
        bpm: info.bpm?.toString() || "",
        genre: mappedGenre,
        key: info.key
          ? keys.find((k) => k.name === info.key) || {
              _id: `custom-key-${info.key}`,
              name: info.key,
              isCustom: true,
            }
          : null,
        tags:
          info.tags?.map((t) => ({
            _id: t._id,
            name: t.tagName || t.name,
          })) || [],
        versions: (info.versions || [])
          .slice()
          .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
          .map((v) => ({
            versionType: v.versionType || "",
            audioFile: v.audioFile || null,
            _id: v._id,
            displayOrder: v.displayOrder,
          })),
      };
    });

    const finalRemixInfos =
      populatedRemixInfos.length > 0
        ? populatedRemixInfos
        : [
            {
              editTitle: null,
              remixer: null,
              additionalMainArtists: [],
              featuredArtist: [],
              releaseDate: "",
              releaseYear: "",
              bpm: "",
              genre: null,
              key: null,
              tags: [],
              versions: [{ versionType: "", audioFile: null }],
            },
          ];

    setForm({
      trackTitle: track.trackTitle || "",
      artist: (track.artist || []).map((a) => ({
        _id: a._id,
        name: a.artistName || a.name,
      })),
      coverArt: track.coverArt?.url
        ? {
            key: track.coverArt.key || "",
            url: track.coverArt.url,
          }
        : null,
      danceability: track.danceability || "",
      energy: track.energy || "",
      popularity: track.popularity || "",
      valence: track.valence || "",
      remixInfos: finalRemixInfos,
    });

    setRemixInfos(finalRemixInfos);
  };

  const handleFileUpload = (file) => {
    setForm({ ...form, coverArt: file });
  };

  const handleAddRemixInfo = () => {
    if (alreadyHasEdit) {
      showToast("error", "You can only add one edit per track.");
      return;
    }
    const existingReleaseYear = remixInfos[0]?.releaseYear || "";
    setRemixInfos([
      ...remixInfos,
      {
        editTitle: null,
        remixer: {
          _id: currentRemixer?.remixerProfileId,
          name: currentRemixer?.remixerName,
        },
        featuredArtist: [],
        additionalMainArtists: [],
        releaseDate: "",
        releaseYear: existingReleaseYear,
        versions: [{ versionType: "", audioFile: null }],
      },
    ]);
  };

  const uploadAudioFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await doUploadToCloudflare({ file, type: "tracks" });
    const data = res.response;

    if (!data || !data.key || !data.url) {
      showToast("error", "Cloudflare failed to produce url/key for audio file");
      return null;
    }
    return {
      key: data.key,
      name: file.name,
      url: data.url,
    };
  };

  const moveVersion = (remixIndex, dragIndex, hoverIndex) => {
    setRemixInfos((prevInfos) => {
      const newInfos = [...prevInfos];
      const versions = [...newInfos[remixIndex].versions];
      const draggedVersion = versions[dragIndex];
      versions.splice(dragIndex, 1);
      versions.splice(hoverIndex, 0, draggedVersion);
      newInfos[remixIndex] = {
        ...newInfos[remixIndex],
        versions
      };
      return newInfos;
    });

    setRemixErrors((prev) => {
       if (!prev || !prev[remixIndex] || !prev[remixIndex].versions) return prev;
       const newErrors = { ...prev };
       const vErrors = [...(newErrors[remixIndex].versions || [])];
       if (vErrors.length > dragIndex) {
         const draggedErr = vErrors[dragIndex];
         vErrors.splice(dragIndex, 1);
         vErrors.splice(hoverIndex, 0, draggedErr);
         newErrors[remixIndex] = { ...newErrors[remixIndex], versions: vErrors };
       }
       return newErrors;
    });
  };

  const uploadCoverArt = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await doUploadToCloudflare({ file, type: "images" });
    const data = res.response;

    if (!data || !data.key || !data.url) {
      showToast("error", "Image upload failed");
    }

    return {
      key: data.key,
      url: data.url,
    };
  };

  const handleSubmit = async () => {
    console.log("Remix Infos on Submit", remixInfos);
    if (!validateFormFields(form, remixInfos)) return;

    // Reset cancellation flag
    isCancelledRef.current = false;

    // Reset publishing steps tracker
    publishingStepsRef.current = [];

    try {
      setIsSubmitting(true);
      publishingStepsRef.current.push("uploading");

      // Step 1: Handle cover art upload (including Spotify URLs)
      if (isCancelledRef.current) throw new Error("Operation cancelled");

      let uploadedCoverArt = null;
      console.log("Form Cover Art", form.coverArt);

      if (form.coverArt) {
        if (form.coverArt.name && form.coverArt instanceof File) {
          // User uploaded file - upload directly
          uploadedCoverArt = await uploadCoverArt(form.coverArt);
        } else if (form.coverArt.url && !form.coverArt.key) {
          // External URL (from Spotify) - download and upload
          console.log("Downloading Spotify cover art from:", form.coverArt.url);

          try {
            const trackTitle = form.trackTitle || "cover-art";
            const artistName = form.artist?.[0]?.name || "unknown";
            const filename = `${trackTitle}-${artistName}`;

            // Download the image from Spotify URL
            const imageFile = await fetchExternalImageAsFile(
              form.coverArt.url,
              filename,
            );

            if (isCancelledRef.current) throw new Error("Operation cancelled");

            // Upload the downloaded image to your database
            uploadedCoverArt = await uploadCoverArt(imageFile);

            console.log(
              "Successfully downloaded and uploaded Spotify cover art",
            );
          } catch (downloadError) {
            console.error(
              "Failed to download Spotify cover art:",
              downloadError,
            );
            showToast(
              "warning",
              "Failed to download cover art from Spotify. Using fallback.",
            );

            // Fallback: keep the original URL if download fails
            uploadedCoverArt = form.coverArt;
          }
        } else if (form.coverArt.key && form.coverArt.url) {
          // Already uploaded - use as is
          uploadedCoverArt = form.coverArt;
        }
      }

      if (isCancelledRef.current) throw new Error("Operation cancelled");

      if (form.coverArt && (!uploadedCoverArt || !uploadedCoverArt.url)) {
        showToast("error", "Cover art upload failed.");
        throw new Error("Cover art upload failed");
      }

      // Step 2: Upload audio files
      const uploadedRemixInfos = await Promise.all(
        remixInfos.map((info) =>
          Promise.all(
            info.versions.map(async (version, versionIndex) => {
              if (isCancelledRef.current)
                throw new Error("Operation cancelled");

              const audio = version.audioFile;
              if (audio && !audio.url) {
                // Upload audio file
                const uploaded = await uploadAudioFile(audio);
                if (isCancelledRef.current)
                  throw new Error("Operation cancelled");

                if (!uploaded || !uploaded.url) {
                  throw new Error("Audio upload failed");
                }

                const peaksRaw = await generatePeaksFromURL(audio);
                if (isCancelledRef.current)
                  throw new Error("Operation cancelled");

                const peaks = Array.isArray(peaksRaw[0])
                  ? peaksRaw[0]
                  : peaksRaw;
                return {
                  ...version,
                  displayOrder: versionIndex,
                  audioFile: {
                    key: uploaded.key,
                    name: uploaded.name,
                    url: uploaded.url,
                    peaks,
                  },
                };
              }

              if (audio && audio.url) {
                return Promise.resolve({
                  ...version,
                  displayOrder: versionIndex,
                  audioFile: audio,
                });
              }
              showToast("error", "Audio file missing.");
              throw new Error("Audio file missing");
            }),
          ).then((uploadedVersions) => ({
            ...info,
            versions: uploadedVersions,
            releaseDate: info.releaseDate
              ? formatDateForBackend(info.releaseDate)
              : localDateAsUtcMidnight(),
          })),
        ),
      );

      if (isCancelledRef.current) throw new Error("Operation cancelled");

      publishingStepsRef.current.push("creating_track");

      // Step 3: Create/Update track
      const updatedArtists = [...form.artist];
      const existingNames = new Set(
        updatedArtists.map((a) => a.name.toLowerCase().trim()),
      );
      console.log("Existing Artist Names:", existingNames);

      uploadedRemixInfos.forEach((info) => {
        (info.featuredArtist || []).forEach((feat) => {
          const name = feat.name?.trim();
          if (name && !existingNames.has(name.toLowerCase())) {
            existingNames.add(name.toLowerCase());
          }
        });
        (info.additionalMainArtists || []).forEach((ama) => {
          const name = ama.name?.trim();
          if (name && !existingNames.has(name.toLowerCase())) {
            existingNames.add(name.toLowerCase());
          }
        });
      });

      const payload = {
        ...form,
        artist: updatedArtists,
        coverArt: {
          key: uploadedCoverArt?.key || form.coverArt?.key || " ",
          url: uploadedCoverArt?.url || form.coverArt?.url || " ",
        },
        remixInfos: uploadedRemixInfos.map((info) => ({
          ...info,
          genre: info.genre,
          tags: info.tags,
        })),
      };

      console.log("Final Payload", payload);

      if (isCancelledRef.current) throw new Error("Operation cancelled");

      const result = await doCreateAndUpdateTrackForm({
        token: auth.token,
        payload,
        trackId,
      });

      if (isCancelledRef.current) throw new Error("Operation cancelled");

      if (result.success) {
        showToast(
          "success",
          `Track ${trackId ? "Updated" : "Published"} successfully.`,
        );

        if (fromAddPlaylist && previousFormData && previousAssignedTracks) {
          const pid = location.state?.playlistId || previousFormData?._id;
          if (pid) {
            navigate(`/edit-playlist/${pid}`, {
              state: {
                formData: previousFormData,
                assignedTracks: previousAssignedTracks,
              },
            });
          } else {
            navigate("/manage-playlist-add-new-playlist", {
              state: {
                formData: previousFormData,
                assignedTracks: previousAssignedTracks,
              },
            });
          }
        } else {
          navigate(isRemixer ? "/remixer-manage-track" : "/manage-track");
        }
      } else {
        showToast("error", result.error.message || "Track saving failed.");
      }
    } catch (err) {
      if (err.message === "Operation cancelled") {
        console.log("Publishing was cancelled");
        //showToast("info", "Publishing cancelled.");
        return;
      }
      showToast("error", err.message || "Something went wrong.");
    } finally {
      // Reset all states
      setIsSubmitting(false);
      publishingStepsRef.current = [];
      isCancelledRef.current = false;
    }
  };

  // Enhanced cancel handler
  const handleCancel = () => {
    const isPublishing =
      isSubmitting ||
      isCheckingDetailedDuplicateTrack ||
      isUploadingToCloudflare ||
      isCreatingAndUpdatingTrack;

    if (isPublishing) {
      const currentStep =
        publishingStepsRef.current[publishingStepsRef.current.length - 1];
      const stepMessages = {
        checking_duplicates: "Checking for duplicates...",
        uploading: "Uploading files...",
        creating_track: "Creating track...",
      };
    }

    // Set cancellation flag
    isCancelledRef.current = true;
    setIsCancelling(true);

    // Reset all loading states immediately
    setIsSubmitting(false);
    showToast("info", "Cancelling publishing...");

    // Small delay to ensure cleanup completes
    setTimeout(() => {
      setIsCancelling(false);
      if (fromAddPlaylist && previousFormData && previousAssignedTracks) {
        const pid = location.state?.playlistId || previousFormData?._id;
        if (pid) {
          navigate(`/edit-playlist/${pid}`, {
            state: {
              formData: previousFormData,
              assignedTracks: previousAssignedTracks,
            },
          });
        } else {
          navigate("/manage-playlist-add-new-playlist", {
            state: {
              formData: previousFormData,
              assignedTracks: previousAssignedTracks,
            },
          });
        }
      } else {
        navigate(isRemixer ? "/remixer-manage-track" : "/manage-track");
      }
    }, 500);
  };

  const validateFormFields = (form, remixInfos) => {
    let hasError = false;
    const errors = {};
    const remixErrorList = {};

    if (
      !form.trackTitle ||
      form.trackTitle.length < 2 ||
      form.trackTitle.length > 100
    ) {
      errors.trackTitle = "Track Title must be between 2 and 100 characters.";
      hasError = true;
    }

    const intFields = [
      { field: "danceability", label: "Danceability" },
      { field: "energy", label: "Energy" },
      { field: "popularity", label: "Popularity" },
      { field: "valence", label: "Valence" },
    ];

    intFields.forEach(({ field, label }) => {
      const value = parseInt(form[field]);
      if (form[field] && (isNaN(value) || value < 0 || value > 100)) {
        errors[field] = `${label} must be between 0 and 100.`;
        hasError = true;
      }
    });

    // if (!form.coverArt ) {
    //   errors.coverArt = "Cover Art is required.";
    //   hasError = true;
    // }

    if (!form.artist || form.artist.length === 0) {
      errors.artist = "At least one Main Artist is required.";
      hasError = true;
    } else {
      const artistValidationErrors = form.artist
        .map((artist) => validateArtistName(artist.name))
        .filter((err) => err);

      if (artistValidationErrors.length > 0) {
        errors.artist = artistValidationErrors[0];
        hasError = true;
      }
    }

    // Track edit titles with their artists for duplicate detection
    const editTitlesWithArtists = new Map();

    remixInfos.forEach((info, i) => {
      const remixErr = {};
      const title = info.editTitle?.trim().toLowerCase();

      if (title) {
        const additionalArtists = (info.additionalMainArtists || [])
          .map((a) => a.name?.toLowerCase().trim())
          .sort();
        const featuredArtists = (info.featuredArtist || [])
          .map((a) => a.name?.toLowerCase().trim())
          .sort();
        const allArtists = `${additionalArtists.join("|")}|${featuredArtists.join("|")}`;

        if (editTitlesWithArtists.has(title)) {
          // Check if any existing entry has the same artists
          const existingEntries = editTitlesWithArtists.get(title);
          const hasSameArtists = existingEntries.some(
            (entry) => entry.allArtists === allArtists,
          );

          if (hasSameArtists) {
            const msg = `Duplicate Edit Title found: "${info.editTitle}" in Edit #${i + 1}.`;
            remixErr.editTitle = msg;
            hasError = true;
          } else {
            // Same title but different artists - allow it
            existingEntries.push({ index: i, allArtists });
          }
        } else {
          // First time seeing this edit title
          editTitlesWithArtists.set(title, [{ index: i, allArtists }]);
        }
      }

      const year = parseInt(info.releaseYear);
      if (info.releaseYear && (isNaN(year) || year < 1000 || year > 9999)) {
        const msg = `Release Year must be a 4-digit year in Edit #${i + 1}.`;
        remixErr.releaseYear = msg;
        hasError = true;
      }

      if (info.releaseDate) {
        console.log("Release Date:", info.releaseDate);
        const date = new Date(info.releaseDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999);

        if (isNaN(date.getTime())) {
          const msg = `Invalid release date in Edit #${i + 1}.`;
          remixErr.releaseDate = msg;
          hasError = true;
        } else if (date > today) {
          const msg = `Release date cannot be in the future in Edit #${i + 1}.`;
          remixErr.releaseDate = msg;
          hasError = true;
        }
      }

      if (!info.genre) {
        const msg = `Genre is required and must be valid in Edit #${i + 1}.`;
        remixErr.genre = msg;
        hasError = true;
      }

      if (!info.key) {
        const msg = `Key is required in Edit #${i + 1}.`;
        remixErr.key = msg;
        hasError = true;
      }

      const bpm = parseInt(info.bpm);
      if (isNaN(bpm) || bpm < 50) {
        const msg = `BPM must be at least 50 in Edit #${i + 1}.`;
        remixErr.bpm = msg;
        hasError = true;
      }

      // Validate remixer name if it exists
      if (info.remixer && info.remixer.name) {
        const remixerError = validateRemixerName(info.remixer.name);
        if (remixerError) {
          remixErr.remixer = remixerError;
          hasError = true;
        }
      }

      // Validate featured artists if they exist
      if (info.featuredArtist && info.featuredArtist.length > 0) {
        const featuredArtistValidationErrors = info.featuredArtist
          .map((artist) => validateArtistName(artist.name))
          .filter((err) => err);

        if (featuredArtistValidationErrors.length > 0) {
          remixErr.featuredArtist = featuredArtistValidationErrors[0];
          hasError = true;
        }
      }

      // Validate additional main artists if they exist
      if (info.additionalMainArtists && info.additionalMainArtists.length > 0) {
        const additionalMainArtistValidationErrors = info.additionalMainArtists
          .map((artist) => validateArtistName(artist.name))
          .filter((err) => err);
        if (additionalMainArtistValidationErrors.length > 0) {
          remixErr.additionalMainArtists =
            additionalMainArtistValidationErrors[0];
          hasError = true;
        }
      }

      const tags = info.tags || [];
      if (tags || tags.length > 0) {
        tags.forEach((tag, j) => {
          const tagValidationErrors = tags
            .map((tag) => validateTag(sanitizeHyphens(tag.name)))
            .filter((err) => err);

          if (tagValidationErrors.length > 0) {
            remixErr.tags = tagValidationErrors[0];
            hasError = true;
            return;
          }
          const allowedPattern = /^[a-zA-Z0-9\s\-&]*$/;
          if (!allowedPattern.test(tag.name)) {
            const msg = `Tag #${j + 1} in Edit #${
              i + 1
            }: Only letters, numbers, spaces, hyphens, and & are allowed.`;
            remixErr.tags = msg;
            hasError = true;
          }
        });
      }

      if (!info.versions || info.versions.length === 0) {
        const msg = `At least one version is required in Edit #${i + 1}.`;
        remixErr.versions = msg;
        hasError = true;
      }

      remixErr.versions = [];
      const seenVersionTypes = new Set();

      info.versions.forEach((version, j) => {
        const verErr = {};

        const versionType = version.versionType?.trim().toLowerCase();
        const fileName = version.audioFile?.name?.trim().toLowerCase();

        if (!versionType) {
          const msg = `Version Type is required in Edit #${i + 1}, Version #${
            j + 1
          }.`;
          verErr.versionType = msg;
          hasError = true;
        } else {
          if (seenVersionTypes.has(versionType)) {
            const msg = `This version type already exists for this edit.`;
            verErr.versionType = msg;
            hasError = true;
          } else {
            seenVersionTypes.add(versionType);
          }
        }

        if (!version.audioFile) {
          const msg = `Audio file is required in Edit #${i + 1}, Version #${
            j + 1
          }.`;
          verErr.audioFile = msg;
          hasError = true;
        }

        remixErr.versions.push(verErr);
      });

      remixErrorList[i] = remixErr;
    });

    setFormErrors(errors);
    setRemixErrors(remixErrorList);
    return !hasError;
  };

  const validateVersionConsistency = (
    extractedMetadata,
    remixIndex,
    form,
    remixInfos,
  ) => {
    const currentRemix = remixInfos[remixIndex];
    const errors = [];
    const expectedTrackTitle = form.trackTitle?.toLowerCase().trim();
    const expectedEditTitle = currentRemix.editTitle?.toLowerCase().trim();

    const expectedMainArtists = form.artist[0]?.name?.toLowerCase().trim();

    console.log("Expected Main Artists:", expectedMainArtists);

    const expectedFeaturedArtists =
      currentRemix.featuredArtist?.map((a) => a.name?.toLowerCase().trim()) ||
      [];

    const expectedAdditionalMainArtists =
      currentRemix.additionalMainArtists?.map((a) =>
        a.name?.toLowerCase().trim(),
      ) || [];

    const extractedTrackTitle = extractedMetadata.title
      ?.replace(/\s*\(.*?\)/g, "")
      .trim()
      .toLowerCase();
    const extractedEditTitle = extractedMetadata.editTitle
      ?.toLowerCase()
      .trim();

    const parsed = parseFullMetadata({
      title: extractedMetadata.title || "",
      contributingArtists: extractedMetadata.artist || "",
    });

    const extractedMainArtists =
      parsed.mainArtists?.map((a) => a.toLowerCase().trim()) || [];
    const extractedAdditionalMainArtists =
      parsed.additionalMainArtists?.map((a) => a.toLowerCase().trim()) || [];
    const extractedFeaturedArtists =
      parsed.featuredArtists?.map((a) => a.toLowerCase().trim()) || [];

    if (
      expectedTrackTitle &&
      extractedTrackTitle &&
      extractedTrackTitle !== expectedTrackTitle
    ) {
      errors.push(
        `Track title mismatch. Expected: "${form.trackTitle}", Found: "${parsed.trackTitle}"`,
      );
    }

    //testing

    if (expectedMainArtists && extractedMainArtists.length > 0) {
      const mainArtistsMatch = expectedMainArtists === extractedMainArtists[0];
      if (!mainArtistsMatch) {
        errors.push(
          `Main artist mismatch. Expected: "${form.artist
            ?.map((a) => a.name)
            .join(", ")}", Found: "${parsed.mainArtists?.join(", ")}"`,
        );
        return errors;
      }
    }

    if (
      expectedEditTitle !== undefined &&
      extractedEditTitle !== expectedEditTitle
    ) {
      if (expectedEditTitle === "" && extractedEditTitle === undefined) {
      } else {
        errors.push(
          `Edit title mismatch. Expected: "${currentRemix.editTitle}", Found: "${extractedMetadata.editTitle}"`,
        );
      }
    } else {
      currentRemix.editTitle = extractedEditTitle || "";
    }

    // if (
    //   expectedFeaturedArtists.length > 0 &&
    //   extractedFeaturedArtists.length > 0
    // ) {
    //   const featuredArtistsMatch =
    //     expectedFeaturedArtists.every((artist) =>
    //       extractedFeaturedArtists.includes(artist),
    //     ) &&
    //     extractedFeaturedArtists.every((artist) =>
    //       expectedFeaturedArtists.includes(artist),
    //     );

    //   if (!featuredArtistsMatch) {
    //     errors.push(
    //       `Featured artists mismatch. Expected: "${currentRemix.featuredArtist
    //         ?.map((a) => a.name)
    //         .join(", ")}", Found: "${parsed.featuredArtists?.join(", ")}"`,
    //     );
    //   }
    // }

    // if (
    //   expectedAdditionalMainArtists.length > 0 &&
    //   extractedAdditionalMainArtists.length > 0
    // ) {
    //   const additionalMainArtistsMatch =
    //     expectedAdditionalMainArtists.every((artist) =>
    //       extractedAdditionalMainArtists.includes(artist),
    //     ) &&
    //     extractedAdditionalMainArtists.every((artist) =>
    //       expectedAdditionalMainArtists.includes(artist),
    //     );

    //   if (!additionalMainArtistsMatch) {
    //     errors.push(
    //       `Additional main artists mismatch. Expected: "${form.additionalMainArtists
    //         ?.map((a) => a.name)
    //         .join(
    //           ", ",
    //         )}", Found: "${parsed.additionalMainArtists?.join(", ")}"`,
    //     );
    //   }
    // }

    return errors;
  };

  const parseFullMetadata = ({ title = "", contributingArtists = "" }) => {
    const result = {
      mainArtists: [],
      additionalMainArtists: [],
      featuredArtists: [],
      trackTitle: "",
      editTitle: null,
      version: "",
    };

    // Process title (brackets)
    const bracketRegex = /\(([^)]+)\)/g;
    const bracketMatches = [...title.matchAll(bracketRegex)];
    if (bracketMatches.length > 0) {
      const reversedMatches = bracketMatches.reverse();
      result.version = reversedMatches[0]?.[1]?.trim() || "";
      if (reversedMatches.length > 1) {
        result.editTitle = reversedMatches[1]?.[1]?.trim() || null;
      }
    }

    result.trackTitle = title.replace(bracketRegex, "").trim();

    // Process artists
    if (contributingArtists) {
      if (contributingArtists.toLowerCase().includes(" ft ")) {
        const [main, featured] = contributingArtists.split(/ ft /i);

        // Process main artists
        const allMainArtists = main
          .split(/,|&|;/)
          .map((s) => s.trim())
          .filter(Boolean);

        // First artist goes to mainArtists, rest go to additionalMainArtists
        if (allMainArtists.length > 0) {
          result.mainArtists = [allMainArtists[0]]; // First artist only
          result.additionalMainArtists = allMainArtists.slice(1); // Rest of the artists
        }

        // Process featured artists
        result.featuredArtists = featured
          .split(/,|&|;/)
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        // No featured artists - split all artists between main and additional
        const allMainArtists = contributingArtists
          .split(/,|&|;/)
          .map((s) => s.trim())
          .filter(Boolean);

        if (allMainArtists.length > 0) {
          result.mainArtists = [allMainArtists[0]]; // First artist only
          result.additionalMainArtists = allMainArtists.slice(1); // Rest of the artists
        }
      }
    }

    return result;
  };

  const getKeyIndex = (keyName) => {
    return keys.findIndex(
      (k) => k.name.toLowerCase() === keyName.toLowerCase()
    );
  };

  const handleExtractedMetaData = async (meta, remixIndex, versionIndex) => {
    if (!meta || !meta.title) return;

    const title = meta.common?.title || meta.title || "";
    const contributingArtists = meta.common?.artist || meta.artist || "";

    const parsed = parseFullMetadata({ title, contributingArtists });

    const extractedMetadata = {
      title: parsed.trackTitle,
      editTitle: parsed.editTitle,
      artist: contributingArtists,
      mainArtists: parsed.mainArtists,
      featuredArtists: parsed.featuredArtists,
      additionalMainArtists: parsed.additionalMainArtists,
    };

    console.log("Extracted Metadata:", extractedMetadata);
    console.log("Remix Infos:", remixInfos);

    // Validate version consistency
    const validationErrors = validateVersionConsistency(
      extractedMetadata,
      remixIndex,
      form,
      remixInfos
    );

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        showToast("error", error);
      });

      const newInfos = [...remixInfos];
      const currentVersions = newInfos[remixIndex]?.versions || [];
      const lastVersionIndex = currentVersions.length - 1;

      if (lastVersionIndex >= 0) {
        currentVersions[lastVersionIndex].audioFile = null;
        setRemixInfos(newInfos);
      }
      return;
    }

    // Check for duplicate edit title if editTitle exists
    if (parsed.editTitle) {
      const editTitleLower = parsed.editTitle.trim().toLowerCase();
      let duplicateEditNumber = -1;
      let hasDifferentArtists = false;

      const isDuplicateEditTitle = remixInfos.some((info, index) => {
        // Skip the current remix being processed
        if (index === remixIndex) return false;

        const existingEditTitle = info.editTitle?.trim().toLowerCase();
        if (existingEditTitle === editTitleLower) {
          duplicateEditNumber = index + 1; // Convert to 1-based numbering

          // Check if additional main artists are different
          const existingAdditionalArtists = (info.additionalMainArtists || [])
            .map((a) => a.name?.toLowerCase().trim())
            .sort();
          const newAdditionalArtists = (parsed.additionalMainArtists || [])
            .map((a) => a.toLowerCase().trim())
            .sort();

          // Check if featured artists are different
          const existingFeaturedArtists = (info.featuredArtist || [])
            .map((a) => a.name?.toLowerCase().trim())
            .sort();
          const newFeaturedArtists = (parsed.featuredArtists || [])
            .map((a) => a.toLowerCase().trim())
            .sort();

          // Compare arrays
          const existingAdditionalStr = existingAdditionalArtists.join("|");
          const newAdditionalStr = newAdditionalArtists.join("|");
          const existingFeaturedStr = existingFeaturedArtists.join("|");
          const newFeaturedStr = newFeaturedArtists.join("|");

          if (
            existingAdditionalStr !== newAdditionalStr ||
            existingFeaturedStr !== newFeaturedStr
          ) {
            hasDifferentArtists = true;
            return false;
          }

          return true;
        }
        return false;
      });

      if (isDuplicateEditTitle) {
        const errorMessage = `This edit title is already used in Edit #${duplicateEditNumber}`;

        showToast("error", errorMessage);

        // Remove the audio file from the current version to prevent processing
        const newInfos = [...remixInfos];
        const currentVersions = newInfos[remixIndex]?.versions || [];
        const lastVersionIndex = currentVersions.length - 1;

        if (lastVersionIndex >= 0) {
          currentVersions[lastVersionIndex].audioFile = null;
        }

        newInfos[remixIndex].editTitle = null;
        setRemixInfos(newInfos);
        return;
      }
    }

    const releaseYear = meta.year?.toString() || "";
    const bpm = meta.bpm?.toString() || "";
    const genreRaw = meta.genre || "";
    const keyRaw = meta.key || "";
    const rawCommentData = meta.comment || meta.comments || "";

    const commentsRaw = Array.isArray(rawCommentData)
      ? rawCommentData
      : typeof rawCommentData === "string"
        ? [rawCommentData]
        : [];

    if (!parsed.trackTitle) {
      return showToast("error", "No valid title found in metadata.");
    }

    if (
      form.trackTitle &&
      parsed.trackTitle.toLowerCase().trim() !==
        form.trackTitle.toLowerCase().trim()
    ) {
      return showToast("error", "Track title mismatch.");
    }

    // Track which fields were populated by metadata
    const fieldsPopulatedByMetadata = {
      [`remix_${remixIndex}`]: {
        editTitle: !!parsed.editTitle,
        featuredArtist: parsed.featuredArtists.length > 0,
        additionalMainArtists: parsed.additionalMainArtists.length > 0,
        releaseYear: !!releaseYear,
        bpm: !!bpm,
        genre: !!genreRaw,
        key: !!keyRaw,
        tags: commentsRaw.length > 0,
      },
    };

    // Update metadata populated fields tracking
    setMetadataPopulatedFields((prev) => ({
      ...prev,
      ...fieldsPopulatedByMetadata,
    }));

    if (!form.trackTitle) {
      setForm((prev) => ({
        ...prev,
        trackTitle: parsed.trackTitle,
        artist: parsed.mainArtists.map((name) => ({ name, isCustom: true })),
      }));

      // Track main form fields populated by metadata
      setMetadataPopulatedFields((prev) => ({
        ...prev,
        mainForm: {
          trackTitle: !!parsed.trackTitle,
          artist: parsed.mainArtists.length > 0,
        },
      }));
    }

    // Add all artists (main, additional main, and featured) to artist options
    [
      ...parsed.mainArtists,
      ...parsed.additionalMainArtists,
      ...parsed.featuredArtists,
    ].forEach((name) => {
      if (
        !artistOptions.some((a) => a.name.toLowerCase() === name.toLowerCase())
      ) {
        setArtistOptions((prev) => [...prev, { name, isCustom: true }]);
      }
    });

    let foundKey = null;
    if (keyRaw) {
      foundKey = keys.find(
        (k) => k.name.toLowerCase() === keyRaw.toLowerCase(),
      );
      if (!foundKey) {
        foundKey = {
          _id: `custom-key-${keyRaw}`,
          name: keyRaw,
          isCustom: true,
        };
        setKeys((prev) => [...prev, foundKey]);
      }
    }

    let validGenre = null;
    if (genreRaw) {
      validGenre = genres.find(
        (g) => g.name.toLowerCase() === genreRaw.toLowerCase(),
      );
    }

    const tagObjects = commentsRaw
      .flatMap((c) => c.split(/,|;/))
      .map((t) => t.trim())
      .filter(Boolean)
      .map((tag) => {
        const existing = tagsOptions.find(
          (tg) => tg.name.toLowerCase() === tag.toLowerCase(),
        );
        if (!existing) {
          const newTag = {
            _id: `custom-tag-${tag}`,
            name: tag,
            isCustom: true,
          };
          setTagsOptions((prev) => [...prev, newTag]);
          return newTag;
        }
        return existing;
      });

    setRemixInfos((prev) => {
      const updated = [...prev];
      const info = updated[remixIndex];

      info.editTitle = parsed.editTitle || info.editTitle;

      // Merge featured artists (avoid duplicates)
      const existingFeaturedArtistNames = new Set(
        (info.featuredArtist || []).map((a) => a.name.toLowerCase())
      );
      const newFeaturedArtists = parsed.featuredArtists
        .filter((name) => !existingFeaturedArtistNames.has(name.toLowerCase()))
        .map((name) => ({
          name,
          isCustom: true,
        }));
      info.featuredArtist = [
        ...(info.featuredArtist || []),
        ...newFeaturedArtists,
      ];

      // Merge additional main artists (avoid duplicates)
      const existingAdditionalArtistNames = new Set(
        (info.additionalMainArtists || []).map((a) => a.name.toLowerCase())
      );
      const newAdditionalArtists = parsed.additionalMainArtists
        .filter(
          (name) => !existingAdditionalArtistNames.has(name.toLowerCase())
        )
        .map((name) => ({
          name,
          isCustom: true,
        }));
      info.additionalMainArtists = [
        ...(info.additionalMainArtists || []),
        ...newAdditionalArtists,
      ];

      info.releaseYear = releaseYear || info.releaseYear;
      info.bpm = bpm || info.bpm;
      info.genre = validGenre || info.genre;
      info.tags = tagObjects.length > 0 ? tagObjects : info.tags;

      if (foundKey) {
        const extractedKeyIndex = getKeyIndex(foundKey.name);
        const currentKeyIndex = info.key
          ? getKeyIndex(info.key.name || info.key)
          : Infinity;

        if (extractedKeyIndex !== -1 && extractedKeyIndex < currentKeyIndex) {
          info.key = foundKey;
        } else if (!info.key) {
          info.key = foundKey;
        }
      }

      // Use the correct versionIndex parameter instead of calculating it
      info.versions[versionIndex] = {
        ...info.versions[versionIndex],
        versionType:
          parsed.version || info.versions[versionIndex].versionType || "",
        audioFile: info.versions[versionIndex].audioFile || null,
      };

      return updated;
    });

    try {
      const isFirstEdit = remixIndex === 0;
      const isCreateMode = !isEditMode;

      if (isCreateMode && isFirstEdit && !didEnrichOnceRef.current) {
        const titleToCheck = parsed.trackTitle || form.trackTitle;

        // Get the first main artist name as a string
        let firstArtistName = null;
        if (parsed.mainArtists?.length > 0) {
          firstArtistName = parsed.mainArtists[0];
        } else if (form.artist?.length > 0) {
          firstArtistName = form.artist[0].name;
        }

        if (titleToCheck && firstArtistName) {
          didEnrichOnceRef.current = true;

          try {
            const dupRes = await doCheckDetailedDuplicateTrack({
              token: auth.token,
              trackTitle: titleToCheck,
              artistNames: [firstArtistName],
            });

            const data = dupRes?.response?.data;
            if (data) {
              if (data.isDuplicate) {
                setFormErrors((prev) => ({
                  ...prev,
                  trackTitle: `Track already exists on ${data.trackId}`,
                }));
                showToast(
                  "error",
                  `A track with this title and artist already exists on ${data.trackId}`,
                );
              } else {
                // Not a duplicate → hydrate features
                if (data.coverArtUrl || data.features || data.releaseDate) {
                  if (data.features) {
                    setForm((prev) => ({
                      ...prev,
                      danceability:
                        data.features.danceability ?? prev.danceability ?? "",
                      energy: data.features.energy ?? prev.energy ?? "",
                      popularity:
                        data.features.popularity ?? prev.popularity ?? "",
                      valence: data.features.valence ?? prev.valence ?? "",
                      coverArt:
                        prev.coverArt && prev.coverArt.key
                          ? prev.coverArt
                          : data.coverArtUrl
                            ? { url: data.coverArtUrl }
                            : null,
                    }));
                  }

                  if (data.releaseDate) {
                    setRemixInfos((prev) =>
                      prev.map((remix, index) =>
                        index === 0
                          ? {
                              ...remix,
                              releaseYear: new Date(data.releaseDate)
                                .getFullYear()
                                .toString(),
                            }
                          : remix,
                      ),
                    );
                  }

                  setFormErrors((prev) => ({
                    ...prev,
                    danceability: "",
                    energy: "",
                    popularity: "",
                    valence: "",
                  }));
                  showToast("success", "Track features fetched successfully.");
                } else {
                  showToast("info", "No features found for this track.");
                }
              }
            }
          } catch (error) {
            // Check if it's a 504 Gateway Timeout error
            const status = error?.response?.status || error?.status;

            if (status === 504) {
              showToast(
                "warning",
                "Connection taking longer than expected. Please retry again.",
              );

              // Reset the ref so user can retry
              didEnrichOnceRef.current = false;
            } else if (
              error?.message?.includes("timeout") ||
              error?.code === "ECONNABORTED"
            ) {
              // Handle client-side timeout
              showToast("warning", "Request timed out. Please try again.");
              didEnrichOnceRef.current = false;
            } else {
              // Handle other errors
              showToast(
                "error",
                "Failed to fetch track features. Please try again.",
              );
              didEnrichOnceRef.current = false;
            }

            console.error("Error fetching track features:", error);
          }
        }
      }
    } catch (e) {
      console.error("duplicate/enrichment step failed:", e);
    }
  };

  // handle audio file removal
  const handleAudioFileRemoval = (remixIndex, versionIndex) => {
    const newInfos = [...remixInfos];
    console.log(
      "Removing audio file for Remix",
      remixIndex,
      "Version",
      versionIndex,
    );

    const isFirstVersion = versionIndex === 0;
    const isFirstEdit = remixIndex === 0;
    const hasMultipleVersions = newInfos[remixIndex].versions.length > 1;

    newInfos[remixIndex].versions[versionIndex].audioFile = null;

    if (isFirstVersion && !isFirstEdit) {
      console.log("Removing edit title for remix", remixIndex);
      newInfos[remixIndex].editTitle = null;

      newInfos[remixIndex].featuredArtist = [];

      newInfos[remixIndex].additionalMainArtists = [];

      newInfos[remixIndex].remixer = null;

      newInfos[remixIndex].releaseDate = "";

      newInfos[remixIndex].releaseYear = "";

      newInfos[remixIndex].bpm = "";

      newInfos[remixIndex].genre = null;

      newInfos[remixIndex].key = null;

      newInfos[remixIndex].tags = [];

      if (hasMultipleVersions) {
        newInfos[remixIndex].versions = newInfos[remixIndex].versions.filter(
          (v, idx) => idx !== versionIndex,
        );
      } else {
        // Reset the single version to empty values
        newInfos[remixIndex].versions[versionIndex] = {
          versionType: "",
          audioFile: null,
        };
      }

      setMetadataPopulatedFields((prev) => {
        const updated = { ...prev };
        delete updated[`remix_${remixIndex}`];
        return updated;
      });
    } else if (isFirstVersion && isFirstEdit) {
      console.log(
        "Removing audio file for first remix and first version",
        remixIndex,
        versionIndex,
      );

      console.log("Removing edit title for first remix", remixIndex);
      newInfos[remixIndex].editTitle = null;

      newInfos[remixIndex].featuredArtist = [];

      newInfos[remixIndex].additionalMainArtists = [];

      newInfos[remixIndex].remixer = null;

      newInfos[remixIndex].releaseDate = null;

      newInfos[remixIndex].releaseYear = null;

      newInfos[remixIndex].bpm = null;

      newInfos[remixIndex].genre = null;

      newInfos[remixIndex].key = null;

      newInfos[remixIndex].tags = [];

      const newForm = { ...form };

      newForm.trackTitle = "";

      newForm.artist = [];

      setForm(newForm);

      setMetadataPopulatedFields((prev) => {
        const updated = { ...prev };
        delete updated[`remix_${remixIndex}`];
        delete updated.mainForm;
        return updated;
      });
    }

    //newInfos[remixIndex].versions[versionIndex].versionType = "";

    setRemixInfos(newInfos);
  };

  const handleRemoveEdit = (indexToRemove) => {
    // Remove the edit from remixInfos
    const newInfos = remixInfos.filter((_, index) => index !== indexToRemove);
    setRemixInfos(newInfos);

    // Clear and reindex errors
    const newErrors = {};
    Object.keys(remixErrors).forEach((key) => {
      const index = parseInt(key);
      if (index < indexToRemove) {
        // Keep errors for indices before the removed one
        newErrors[index] = remixErrors[index];
      } else if (index > indexToRemove) {
        // Shift errors for indices after the removed one
        newErrors[index - 1] = remixErrors[index];
      }
      // Skip errors for the removed index
    });

    setRemixErrors(newErrors);
    setShowDeleteModal(false);
    setPendingDeleteIndex(null);
  };

  // Handler for removing a version (second remove icon)
  const handleRemoveVersion = (remixIndex, versionIndexToRemove) => {
    // Remove the version from the specific remix
    const newInfos = [...remixInfos];
    newInfos[remixIndex].versions = newInfos[remixIndex].versions.filter(
      (_, index) => index !== versionIndexToRemove,
    );
    setRemixInfos(newInfos);

    const newErrors = { ...remixErrors };

    if (newErrors[remixIndex]?.versions) {
      const newVersionErrors = {};
      newErrors[remixIndex].versions.forEach((versionError, index) => {
        if (index < versionIndexToRemove) {
          newVersionErrors[index] = versionError;
        } else if (index > versionIndexToRemove) {
          newVersionErrors[index - 1] = versionError;
        }
      });

      newErrors[remixIndex] = {
        ...newErrors[remixIndex],
        versions: Object.values(newVersionErrors),
      };
    }

    setRemixErrors(newErrors);
    setShowVersionDeleteModal(false);
    setPendingVersionDelete(null);
  };

  const handleRefetch = async () => {
    try {
      // Get track title and first artist
      const trackTitle = form.trackTitle?.trim();
      const firstArtist = form.artist?.[0]?.name?.trim();
      console.log("Refetching for:", { trackTitle, firstArtist });
      if (!trackTitle || !firstArtist) {
        showToast("error", "Track title and artist are required for refetch");
        return;
      }

      const response = await doRefetchTrackFeatures({
        trackTitle,
        artistName: firstArtist,
      });

      console.log("Refetch result:", response);
      const result = response?.response;

      if (response.success && result.data) {
        const data = result.data;

        if (!data.coverArtUrl && !data.features && !data.releaseDate) {
          showToast("error", "No features found for this track");
          return;
        }

        // Update form with refetched data
        setForm((prev) => ({
          ...prev,
          danceability: data.features?.danceability ?? prev.danceability ?? "",
          energy: data.features?.energy ?? prev.energy ?? "",
          popularity: data.features?.popularity ?? prev.popularity ?? "",
          valence: data.features?.valence ?? prev.valence ?? "",
          coverArt: data.coverArtUrl
            ? { url: data.coverArtUrl }
            : prev.coverArt,
        }));

        // Update release date in ALL remixInfos
        if (data.releaseDate) {
          setRemixInfos((prev) =>
            prev.map((remix) => ({
              ...remix,
              //releaseDate: data.releaseDate,
              releaseYear: new Date(data.releaseDate).getFullYear().toString(),
            })),
          );
        }

        // Clear any form errors
        setFormErrors((prev) => ({
          ...prev,
          danceability: "",
          energy: "",
          popularity: "",
          valence: "",
        }));

        showToast("success", "Track features refetched successfully!");
      } else {
        showToast(
          "error",
          result.message || "Failed to refetch track features",
        );
      }
    } catch (error) {
      console.error("Refetch error:", error);
      showToast("error", "An error occurred while refetching features");
    }
  };

  console.log(remixInfos);

  const alreadyHasEdit = isRemixer
    ? remixInfos.some(
        (info) => info.remixer?._id === currentRemixer?.remixerProfileId,
      )
    : false;

  const hasErrors =
    Object.values(formErrors).some(Boolean) ||
    Object.values(remixErrors).some((remix) =>
      typeof remix === "object"
        ? Object.values(remix).some((field) => {
            if (Array.isArray(field)) {
              return field.some((v) =>
                typeof v === "object"
                  ? Object.values(v).some(Boolean)
                  : Boolean(v),
              );
            }
            return Boolean(field);
          })
        : Boolean(remix),
    );

  return (
    <div className="bg-[#1C1C1C] text-white min-h-screen py-10 px-6">
      {/* Release Info */}
      <div className="bg-[#111111D6] border-[#505050] border-[1px] p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-[20px] text-[#E3E3E3] font-Public_Sans font-[700] mb-4">
          Release Info
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Track Title"
            isRequired={true}
            error={formErrors.trackTitle}
            placeholder="Enter Track Title"
            value={form.trackTitle}
            onChange={(e) => setForm({ ...form, trackTitle: e.target.value })}
            inputStyle={formStyles.addTrackInput}
            onFocus={() =>
              setFormErrors((prev) => ({ ...prev, trackTitle: "" }))
            }
            labelStyle="text-[14px]"
            readOnly={isRemixerEditing}
            className={isRemixerEditing ? "cursor-default" : ""}
          />
          <div
            className={isRemixerEditing ? "pointer-events-none opacity-90" : ""}
          >
            <FileUpload
              label="Cover Art"
              type="image"
              error={formErrors.coverArt}
              defaultValue={form.coverArt}
              //isRequired={true}
              showPreview={true}
              onFileUpload={handleFileUpload}
              uploadText="Upload cover art (Jpeg, Jpg, png)"
              maxSize={5}
              allowRemove={!isRemixerEditing}
              onRemove={() => setForm({ ...form, coverArt: null })}
              onFocus={() =>
                setFormErrors((prev) => ({ ...prev, coverArt: "" }))
              }
            />
          </div>
          <div className="col-span-2">
            {!isRemixerEditing ? (
              <MultiSelectedDropDownWithInput
                label="Main Artist(s)"
                error={formErrors.artist}
                options={artistOptions}
                onNewCustomItem={(item) =>
                  setArtistOptions((prev) => [...prev, item])
                }
                defaultSelected={form.artist}
                onSelect={(selected) => {
                  const errorMessages = selected
                    .map((artist) => validateArtistName(artist.name))
                    .filter((err) => err);

                  setForm({ ...form, artist: selected });

                  setFormErrors((prev) => ({
                    ...prev,
                    artist: errorMessages.length > 0 ? errorMessages[0] : "",
                  }));
                }}
                displayKey="name"
                idKey="_id"
                isRequired={true}
                onFocus={() =>
                  setFormErrors((prev) => ({ ...prev, artist: "" }))
                }
                validUniqueName={true}
              />
            ) : (
              <Input
                label="Main Artist(s)"
                value={
                  form.artist?.length > 0
                    ? form.artist.map((a) => a.name).join(", ")
                    : ""
                }
                readOnly
                inputStyle={formStyles.addTrackInput}
                labelStyle="text-[14px]"
                className="cursor-default"
              />
            )}
          </div>
          <Input
            type="number"
            validateRange={{ min: 0, max: 100, isInteger: true }}
            label="Danceability"
            error={formErrors.danceability}
            placeholder="Enter danceability"
            value={form.danceability}
            onChange={(e) => setForm({ ...form, danceability: e.target.value })}
            inputStyle={formStyles.addTrackInput}
            onFocus={() =>
              setFormErrors((prev) => ({ ...prev, danceability: "" }))
            }
            labelStyle="text-[14px]"
            readOnly={isRemixerEditing}
            className={isRemixerEditing ? "cursor-default" : ""}
            onKeyDown={(e) => {
              if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const paste = e.clipboardData.getData("text");
              if (!/^\d+$/.test(paste)) {
                e.preventDefault();
              }
            }}
          />
          <Input
            type="number"
            validateRange={{ min: 0, max: 100, isInteger: true }}
            label="Energy"
            error={formErrors.energy}
            placeholder="Enter energy"
            value={form.energy}
            onChange={(e) => setForm({ ...form, energy: e.target.value })}
            inputStyle={formStyles.addTrackInput}
            onFocus={() => setFormErrors((prev) => ({ ...prev, energy: "" }))}
            labelStyle="text-[14px]"
            readOnly={isRemixerEditing}
            className={isRemixerEditing ? "cursor-default" : ""}
            onKeyDown={(e) => {
              if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const paste = e.clipboardData.getData("text");
              if (!/^\d+$/.test(paste)) {
                e.preventDefault();
              }
            }}
          />
          <Input
            label="Popularity"
            type="number"
            validateRange={{ min: 0, max: 100, isInteger: true }}
            placeholder="Enter popularity"
            error={formErrors.popularity}
            value={form.popularity}
            onChange={(e) => setForm({ ...form, popularity: e.target.value })}
            inputStyle={formStyles.addTrackInput}
            onFocus={() =>
              setFormErrors((prev) => ({ ...prev, popularity: "" }))
            }
            labelStyle="text-[14px]"
            readOnly={isRemixerEditing}
            className={isRemixerEditing ? "cursor-default" : ""}
            onKeyDown={(e) => {
              if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const paste = e.clipboardData.getData("text");
              if (!/^\d+$/.test(paste)) {
                e.preventDefault();
              }
            }}
          />
          <Input
            label="Valence"
            type="number"
            validateRange={{ min: 0, max: 100, isInteger: true }}
            placeholder="Enter valence"
            error={formErrors.valence}
            value={form.valence}
            onChange={(e) => setForm({ ...form, valence: e.target.value })}
            inputStyle={formStyles.addTrackInput}
            onFocus={() => setFormErrors((prev) => ({ ...prev, valence: "" }))}
            labelStyle="text-[14px]"
            readOnly={isRemixerEditing}
            className={isRemixerEditing ? "cursor-default" : ""}
            onKeyDown={(e) => {
              if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const paste = e.clipboardData.getData("text");
              if (!/^\d+$/.test(paste)) {
                e.preventDefault();
              }
            }}
          />
        </div>
        <div className="flex justify-center items-center mt-[20px]">
          <div>
            <CustomButton
              textSize="text-[18px]"
              padding="py-[8px]"
              className="my-5 h-[40px] w-[161px] font-inter"
              borderColor="border-[#51F9EF] round-[4px]"
              type="button"
              loading={isRefetchingTrackFeatures}
              disabled={
                isSubmitting ||
                isCheckingDetailedDuplicateTrack ||
                isUploadingToCloudflare ||
                isCreatingAndUpdatingTrack ||
                hasErrors ||
                isCancelling ||
                isRefetchingTrackFeatures
              }
              onClick={handleRefetch}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 16H19V21M10 8H5.00003V3M19.418 9.003C18.8574 7.61519 17.9186 6.41251 16.7084 5.53166C15.4983 4.65082 14.0653 4.12714 12.5723 4.02016C11.0793 3.91318 9.5863 4.22718 8.26291 4.92648C6.93952 5.62577 5.83886 6.68231 5.08603 7.976M4.58203 14.997C5.14283 16.3846 6.0816 17.587 7.29166 18.4676C8.50172 19.3483 9.93455 19.8718 11.4273 19.9788C12.9201 20.0858 14.4129 19.7719 15.7362 19.0728C17.0595 18.3737 18.1601 17.3174 18.913 16.024"
                  stroke="#51F9EF"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Re-Fetch
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Remix/Edit Info Sections */}
      <div className="border-[1px] rounded-lg border-[#505050]">
        {remixInfos.map((info, remixIndex) => {
          const isOwnerOfEdit = isRemixer
            ? info?.remixer?._id === currentRemixer?.remixerProfileId
            : true;

          return (
            <div
              key={remixIndex}
              className={`bg-[#111111D6] p-[20px] shadow-md ${
                remixIndex === 0 ? "rounded-t-lg" : ""
              }`}
            >
              <div className="flex justify-between items-center">
                <h2 className="text-[20px] font-[700] text-[#E3E3E3] mb-[12px]">
                  {remixIndex === 0 ? "Edit / Remix Info" : ""}
                </h2>
                {isOwnerOfEdit && remixIndex !== 0 && (
                  <button
                    onClick={() => {
                      setPendingDeleteIndex(remixIndex);
                      setShowDeleteModal(true);
                    }}
                    className="text-red-500 mb-[12px]"
                  >
                    <img
                      src={removeIcon}
                      alt="Remove Icon"
                      className="w-[24px] h-[20px] object-cover"
                    />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-[1px] border-[#505050] p-4 rounded-lg">
                <Input
                  label="Edit Title"
                  placeholder="Enter edit title"
                  maxLength={100}
                  value={info.editTitle}
                  error={remixErrors?.[remixIndex]?.editTitle}
                  onChange={(e) => {
                    const newInfos = [...remixInfos];
                    newInfos[remixIndex].editTitle = e.target.value;
                    setRemixInfos(newInfos);
                  }}
                  inputStyle={formStyles.addTrackInput}
                  onFocus={() =>
                    setRemixErrors((prev) => ({
                      ...prev,
                      [remixIndex]: {
                        ...prev[remixIndex],
                        editTitle: "",
                      },
                    }))
                  }
                  labelStyle="text-[14px]"
                  readOnly={!isOwnerOfEdit}
                  className={!isOwnerOfEdit ? "cursor-default" : ""}
                />
                {isRemixer || !isOwnerOfEdit ? (
                  <Input
                    label="Remixer"
                    placeholder="Select Remixer"
                    value={info.remixer?.name}
                    readOnly
                    inputStyle={formStyles.addTrackInput}
                    labelStyle="text-[14px]"
                    className="cursor-default"
                  />
                ) : (
                  <DropdownWithInput
                    label="Remixer"
                    options={
                      isRemixer
                        ? [] // prevent changing
                        : remixers.filter((r) => r._id !== info.remixer?._id)
                    }
                    dropdownTyping={true}
                    defaultOption={info.remixer}
                    error={remixErrors?.[remixIndex]?.remixer}
                    onSelect={(selected) => {
                      let errorMessage = "";
                      if (selected && selected.name) {
                        errorMessage = validateRemixerName(selected.name);
                      }

                      const newInfos = [...remixInfos];
                      newInfos[remixIndex].remixer = selected;
                      setRemixInfos(newInfos);

                      setRemixErrors((prev) => ({
                        ...prev,
                        [remixIndex]: {
                          ...prev[remixIndex],
                          remixer: errorMessage,
                        },
                      }));
                    }}
                    placeholder="Choose remixer"
                    displayKey="name"
                    idKey="_id"
                    key={`remixer-${remixIndex}-${info.remixer?._id || "null"}`}
                    onFocus={() =>
                      setRemixErrors((prev) => ({
                        ...prev,
                        [remixIndex]: {
                          ...prev[remixIndex],
                          remixer: "",
                        },
                      }))
                    }
                    isSearchable={true}
                    buttonClassName="h-[45px]"
                    allowClear={!isRemixerEditing}
                  />
                )}
                <div className="col-span-2">
                  {isOwnerOfEdit ? (
                    <MultiSelectedDropDownWithInput
                      label="Additional Main Artist(s)"
                      options={artistOptions}
                      defaultSelected={info.additionalMainArtists}
                      error={remixErrors?.[remixIndex]?.additionalMainArtist}
                      onSelect={(selected) => {
                        const errorMessages = selected
                          .map((artist) => validateArtistName(artist.name))
                          .filter((err) => err);

                        const newInfos = [...remixInfos];
                        newInfos[remixIndex].additionalMainArtists = selected;
                        setRemixInfos(newInfos);

                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            additionalMainArtist:
                              errorMessages.length > 0 ? errorMessages[0] : "",
                          },
                        }));
                      }}
                      displayKey="name"
                      idKey="_id"
                      validUniqueName={true}
                      placeholder="Select additional main artist(s)"
                      onFocus={() =>
                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            additionalMainArtist: "",
                          },
                        }))
                      }
                    />
                  ) : (
                    <Input
                      label="Additional Main Artist(s)"
                      value={
                        info.additionalMainArtists?.length > 0
                          ? info.additionalMainArtists
                              .map((a) => a.name)
                              .join(", ")
                          : ""
                      }
                      readOnly
                      inputStyle={formStyles.addTrackInput}
                      labelStyle="text-[14px]"
                      className="cursor-default"
                    />
                  )}
                </div>
                <div className="col-span-2">
                  {isOwnerOfEdit ? (
                    <MultiSelectedDropDownWithInput
                      label="Featured Artist(s)"
                      options={artistOptions}
                      defaultSelected={info.featuredArtist}
                      error={remixErrors?.[remixIndex]?.featuredArtist}
                      onSelect={(selected) => {
                        const errorMessages = selected
                          .map((artist) => validateArtistName(artist.name))
                          .filter((err) => err);

                        const newInfos = [...remixInfos];
                        newInfos[remixIndex].featuredArtist = selected;
                        setRemixInfos(newInfos);

                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            featuredArtist:
                              errorMessages.length > 0 ? errorMessages[0] : "",
                          },
                        }));
                      }}
                      displayKey="name"
                      idKey="_id"
                      validUniqueName={true}
                      placeholder="Select featured artist(s)"
                      onFocus={() =>
                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            featuredArtist: "",
                          },
                        }))
                      }
                    />
                  ) : (
                    <Input
                      label="Featured Artist(s)"
                      value={
                        info.featuredArtist?.length > 0
                          ? info.featuredArtist.map((a) => a.name).join(", ")
                          : ""
                      }
                      readOnly
                      inputStyle={formStyles.addTrackInput}
                      labelStyle="text-[14px]"
                      className="cursor-default"
                    />
                  )}
                </div>
                <Input
                  label="Edit Release Year"
                  type="number"
                  placeholder="Enter year (YYYY)"
                  value={info.releaseYear}
                  error={remixErrors?.[remixIndex]?.releaseYear}
                  validateRange={{ min: 1000, max: 9999, isInteger: true }}
                  maxLength={4}
                  onChange={(e) => {
                    const newInfos = [...remixInfos];
                    newInfos[remixIndex].releaseYear = e.target.value;
                    setRemixInfos(newInfos);
                  }}
                  inputStyle={`${formStyles.addTrackInput} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                  onFocus={() =>
                    setRemixErrors((prev) => ({
                      ...prev,
                      [remixIndex]: {
                        ...prev[remixIndex],
                        releaseYear: "",
                      },
                    }))
                  }
                  labelStyle="text-[14px]"
                  readOnly={!isOwnerOfEdit}
                  className={!isOwnerOfEdit ? "cursor-default" : ""}
                  onKeyDown={(e) => {
                    if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const paste = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(paste)) {
                      e.preventDefault();
                    }
                  }}
                />
                {isOwnerOfEdit ? (
                  <Dropdown
                    label="Genre"
                    options={genres}
                    isRequired={true}
                    defaultOption={info.genre}
                    error={remixErrors?.[remixIndex]?.genre}
                    onSelect={(selected) => {
                      const newInfos = [...remixInfos];
                      newInfos[remixIndex].genre = selected;
                      setRemixInfos(newInfos);
                    }}
                    placeholder="Select genre"
                    displayKey="name"
                    idKey="_id"
                    key={`genre-${remixIndex}-${info.genre?._id || "null"}`}
                    onFocus={() =>
                      setRemixErrors((prev) => ({
                        ...prev,
                        [remixIndex]: {
                          ...prev[remixIndex],
                          genre: null,
                        },
                      }))
                    }
                  />
                ) : (
                  <Input
                    label="Genre"
                    placeholder="Select Genre"
                    value={info.genre?.name || ""}
                    readOnly
                    inputStyle={formStyles.addTrackInput}
                    labelStyle="text-[14px]"
                    className="cursor-default"
                  />
                )}
                <Input
                  label="BPM"
                  type="number"
                  placeholder="Enter BPM"
                  validateRange={{ min: 50, max: 160, isInteger: true }}
                  value={info.bpm}
                  error={remixErrors?.[remixIndex]?.bpm}
                  onChange={(e) => {
                    const newInfos = [...remixInfos];
                    newInfos[remixIndex].bpm = e.target.value;
                    setRemixInfos(newInfos);
                  }}
                  isRequired={true}
                  inputStyle={formStyles.addTrackInput}
                  onFocus={() =>
                    setRemixErrors((prev) => ({
                      ...prev,
                      [remixIndex]: {
                        ...prev[remixIndex],
                        bpm: "",
                      },
                    }))
                  }
                  labelStyle="text-[14px]"
                  readOnly={!isOwnerOfEdit}
                  className={!isOwnerOfEdit ? "cursor-default" : ""}
                  onKeyDown={(e) => {
                    if (["e", "E", ".", ",", "-", "+"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const paste = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(paste)) {
                      e.preventDefault();
                    }
                  }}
                />
                {isOwnerOfEdit ? (
                  <Dropdown
                    label="Key"
                    options={keys}
                    isRequired={true}
                    defaultOption={info.key}
                    error={remixErrors?.[remixIndex]?.key}
                    onSelect={(selected) => {
                      const newInfos = [...remixInfos];
                      newInfos[remixIndex].key = selected;
                      setRemixInfos(newInfos);
                    }}
                    placeholder="Select key"
                    displayKey="name"
                    idKey="_id"
                    key={`key-${remixIndex}-${info.key?._id || "null"}`}
                    onFocus={() =>
                      setRemixErrors((prev) => ({
                        ...prev,
                        [remixIndex]: {
                          ...prev[remixIndex],
                          key: "",
                        },
                      }))
                    }
                    isSearchable={true}
                  />
                ) : (
                  <Input
                    label="Key"
                    value={info.key?.name || ""}
                    readOnly
                    inputStyle={formStyles.addTrackInput}
                    labelStyle="text-[14px]"
                    className="cursor-default"
                  />
                )}
                <div className="col-span-2">
                  {isOwnerOfEdit ? (
                    <MultiSelectedDropDownWithInput
                      label="Tags"
                      options={tagsOptions}
                      defaultSelected={info.tags}
                      onSelect={(selected) => {
                        const sanitized = selected.map((t) => ({
                          ...t,
                          name: sanitizeHyphens(t.name.trim()),
                        }));

                        const errorMessages = sanitized
                          .map((tag) => validateTag(tag.name))
                          .filter((err) => err);

                        const newInfos = [...remixInfos];
                        newInfos[remixIndex].tags = sanitized;
                        setRemixInfos(newInfos);

                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            tags:
                              errorMessages.length > 0 ? errorMessages[0] : "",
                          },
                        }));
                      }}
                      validUniqueName={true}
                      displayKey="name"
                      idKey="_id"
                      error={remixErrors?.[remixIndex]?.tags}
                      onFocus={() =>
                        setRemixErrors((prev) => ({
                          ...prev,
                          [remixIndex]: {
                            ...prev[remixIndex],
                            tags: "",
                          },
                        }))
                      }
                      placeholder="Select tags"
                    />
                  ) : (
                    <Input
                      label="Tags"
                      value={
                        info.tags?.length > 0
                          ? info.tags.map((a) => a.name).join(", ")
                          : ""
                      }
                      readOnly
                      inputStyle={formStyles.addTrackInput}
                      labelStyle="text-[14px]"
                      className="cursor-default"
                    />
                  )}
                </div>
                <Input
                  label="Edit Added Date"
                  type="date"
                  validateTodayAndPast={true}
                  value={info.releaseDate}
                  isLocalUTC={true}
                  error={remixErrors?.[remixIndex]?.releaseDate}
                  onChange={(e) => {
                    const newInfos = [...remixInfos];
                    newInfos[remixIndex].releaseDate = e.target.value;
                    setRemixInfos(newInfos);
                  }}
                  inputStyle={formStyles.addTrackInput}
                  onFocus={() =>
                    setRemixErrors((prev) => ({
                      ...prev,
                      [remixIndex]: {
                        ...prev[remixIndex],
                        releaseDate: "",
                      },
                    }))
                  }
                  labelStyle="text-[14px]"
                  readOnly={!isOwnerOfEdit}
                />
                {/* Upload Versions for this remix */}
                <div className="col-span-2 rounded-lg border-[1px] border-[#505050] p-4">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Upload Version
                  </h3>
                  <DndProvider backend={HTML5Backend}>
                  {info.versions.map((upload, versionIndex) => (
                    <DraggableVersionRow
                      key={`version-${remixIndex}-${versionIndex}`}
                      versionIndex={versionIndex}
                      remixIndex={remixIndex}
                      moveVersion={moveVersion}
                      isOwnerOfEdit={isOwnerOfEdit}
                    >
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col md:flex-row gap-4 items-start w-full">
                        <div className="w-full md:w-1/2 ">
                          {isOwnerOfEdit ? (
                            <Input
                              label="Version Type"
                              isRequired={true}
                              placeholder="Enter version type"
                              value={upload.versionType || ""}
                              error={
                                remixErrors?.[remixIndex]?.versions?.[
                                  versionIndex
                                ]?.versionType
                              }
                              onChange={(e) => {
                                const newInfos = [...remixInfos];
                                newInfos[remixIndex].versions[
                                  versionIndex
                                ].versionType = e.target.value;
                                setRemixInfos(newInfos);
                              }}
                              inputStyle={formStyles.addTrackInput}
                              onFocus={() =>
                                setRemixErrors((prev) => ({
                                  ...prev,
                                  [remixIndex]: {
                                    ...(prev[remixIndex] || {}),
                                    versions: [
                                      ...(prev[remixIndex]?.versions || []),
                                    ].map((version, idx) =>
                                      idx === versionIndex
                                        ? { ...version, versionType: "" }
                                        : version,
                                    ),
                                  },
                                }))
                              }
                              labelStyle="text-[14px]"
                            />
                          ) : (
                            <Input
                              label="Version Type"
                              value={upload.versionType || ""}
                              readOnly
                              inputStyle={formStyles.addTrackInput}
                              labelStyle="text-[14px]"
                              className="cursor-default"
                            />
                          )}
                        </div>

                        <div className="w-full md:w-1/2 pb-2">
                          <div className="flex justify-between items-center mb-[6px]">
                            <div className="text-sm mb-[5px] font-medium">
                              Audio File
                              <span className="text-light-red">*</span>
                            </div>
                            <div className="flex-end">
                              {isOwnerOfEdit && info.versions.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPendingVersionDelete({
                                      remixIndex,
                                      versionIndex,
                                    });
                                    setShowVersionDeleteModal(true);
                                  }}
                                >
                                  <img
                                    src={removeIcon}
                                    alt="Remove Icon"
                                    className="w-[24px] h-[20px] object-cover"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <div
                            className={
                              !isOwnerOfEdit
                                ? "pointer-events-none opacity-90"
                                : ""
                            }
                          >
                            <FileUpload
                              label=""
                              isRequired
                              maxSize={100}
                              error={
                                remixErrors?.[remixIndex]?.versions?.[
                                  versionIndex
                                ]?.audioFile
                              }
                              defaultValue={
                                info.versions[versionIndex].audioFile
                              }
                              expectedTitle={form.trackTitle}
                              onReject={(wrongTitle) =>
                                showToast(
                                  "error",
                                  `Rejected: ${wrongTitle} does not match track title`,
                                )
                              }
                              uploadText="Select your file or drag and drop here"
                              type="audio"
                              onFileUpload={
                                isOwnerOfEdit
                                  ? (file) => {
                                      const newInfos = [...remixInfos];
                                      console.log("Uploading file:", file);
                                      console.log(
                                        "Remix Index:",
                                        remixIndex,
                                        "Version Index:",
                                        versionIndex,
                                      );

                                      // Add bounds checking to prevent the error
                                      if (
                                        newInfos[remixIndex] &&
                                        newInfos[remixIndex].versions &&
                                        newInfos[remixIndex].versions[
                                          versionIndex
                                        ]
                                      ) {
                                        newInfos[remixIndex].versions[
                                          versionIndex
                                        ].audioFile = file;
                                        setRemixInfos(newInfos);
                                      } else {
                                        console.error(
                                          "Invalid remix or version index:",
                                          remixIndex,
                                          versionIndex,
                                        );
                                      }
                                    }
                                  : undefined
                              }
                              onMetaExtracted={
                                isOwnerOfEdit
                                  ? (meta) =>
                                      handleExtractedMetaData(
                                        meta,
                                        remixIndex,
                                        versionIndex,
                                      )
                                  : undefined
                              }
                              allowRemove={isOwnerOfEdit}
                              onRemove={
                                isOwnerOfEdit
                                  ? () =>
                                      handleAudioFileRemoval(
                                        remixIndex,
                                        versionIndex,
                                      )
                                  : undefined
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    </DraggableVersionRow>
                  ))}
                  </DndProvider>
                  <div className="flex flex-row justify-center items-center mt-5">
                    {isOwnerOfEdit && (
                      <CustomButton
                        textSize="text-[18px]"
                        padding="py-[6.5px] px-[2px]"
                        className="my-5 h-[40px] w-[130px] font-inter"
                        borderColor="border-[#51F9EF]"
                        onClick={() => {
                          const newInfos = [...remixInfos];
                          newInfos[remixIndex].versions.push({
                            versionType: "",
                            audioFile: null,
                          });
                          setRemixInfos(newInfos);
                        }}
                      >
                        + Add Version
                      </CustomButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {(!isRemixer || !alreadyHasEdit) && (
          <div className="flex flex-col justify-center items-center mt-[20px] mb-[70px]">
            <CustomButton
              useGradientBorder
              rounded="rounded-lg"
              textSize="text-[18px]"
              padding="py-[6.5px] px-[30px]"
              font="font-jost"
              border="border-lg"
              type="button"
              className="w-[174px] h-[45px] font-jost"
              onClick={handleAddRemixInfo}
            >
              + Add Edit
            </CustomButton>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 justify-center items-center gap-2 my-[56px]">
        <div className="flex justify-end">
          <CustomButton
            textColor="text-[#818181]"
            borderColor="border-[#818181] border-[2px]"
            textSize="text-[18px]"
            font="font-jost"
            padding="py-[6.5px] px-[36px]"
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {isCancelling ? "Cancelling..." : "Cancel"}
          </CustomButton>
        </div>
        <div>
          <CustomButton
            useGradientBorder
            textSize="text-[18px]"
            font="font-jost"
            padding="py-[6.5px] px-[36px]"
            rounded="rounded-lg"
            border="border-lg"
            type="button"
            loading={
              isSubmitting ||
              isUploadingToCloudflare ||
              isCreatingAndUpdatingTrack ||
              isCancelling
            }
            disabled={
              isSubmitting ||
              isCheckingDetailedDuplicateTrack ||
              isUploadingToCloudflare ||
              isCreatingAndUpdatingTrack ||
              hasErrors ||
              isCancelling
            }
            onClick={handleSubmit}
          >
            Publish
          </CustomButton>
        </div>
      </div>
      {showDeleteModal && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          onCancel={() => {
            setShowDeleteModal(false);
            setPendingDeleteIndex(null);
          }}
          onConfirm={() => handleRemoveEdit(pendingDeleteIndex)}
          title="Confirm Deletion"
          content="Are you sure you want to delete this edit/remix?"
        />
      )}

      {showVersionDeleteModal && (
        <ConfirmDeleteModal
          visible={showVersionDeleteModal}
          onCancel={() => {
            setShowVersionDeleteModal(false);
            setPendingVersionDelete({ remixIndex: null, versionIndex: null });
          }}
          onConfirm={() => {
            const { remixIndex, versionIndex } = pendingVersionDelete;
            if (remixIndex !== null && versionIndex !== null) {
              handleRemoveVersion(remixIndex, versionIndex);
            }
          }}
          title="Confirm Deletion"
          content="Are you sure you want to delete this version?"
        />
      )}
    </div>
  );
};

export default AddNewTracks;
